// The optional step under the first-run checklist (#602): connect Jira or Slack.
//
// It is not one of `setupSteps()`'s rows and is never counted with them; the
// decisions on who sees it and what it reads are on #602. Everything here is pure
// or takes its `Api`, so the two screens that show the checklist render the same
// row: `RunsPage` from the console's reducer, `OverviewPage` from its own state,
// each reading it through `useIntegrationsStep`, the one place that says when (#627).

import { useEffect, useRef } from "react";

import type { Api } from "../api/http";
import { listIntegrations, type AdapterKind, type IntegrationRow } from "../api/integrations";
import { listRoles, roleHolds, type Role } from "../api/roles";
import { SLACK_INSTALL_PARAM, SLACK_INSTALLATION_PARAM, slackInstallResultOf } from "../api/slackInstall";
import { onOwnMembership, type SessionResponse } from "../api/session";
import { destinationOf } from "./routes";

/** Which kinds already hold a live connection. */
export type IntegrationsStep = Record<AdapterKind, boolean>;

/** The query parameter a link into a connecting flow carries. */
const CONNECT_PARAM = "connect";

const KINDS: readonly AdapterKind[] = ["jira", "slack"];

/** Where the checklist sends a person: the Integrations page (#805), with that kind's flow open. */
export const connectHref = (kind: AdapterKind): string =>
  `${destinationOf("integrations").path}?${CONNECT_PARAM}=${kind}`;

/**
 * The flow a location asks for, or null: an unknown kind asks for nothing. A return from
 * Add to Slack (#937) asks for the Slack flow, which resumes from what the return says.
 */
export function connectRequestOf(search: string): AdapterKind | null {
  if (slackInstallResultOf(search) !== null) return "slack";
  const asked = new URLSearchParams(search).get(CONNECT_PARAM);
  return KINDS.find((kind) => kind === asked) ?? null;
}

/** The same location without the request, so a reload or Back does not ask again. */
export function withoutConnectRequest(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.delete(CONNECT_PARAM);
  params.delete(SLACK_INSTALL_PARAM);
  params.delete(SLACK_INSTALLATION_PARAM);
  const rest = params.toString();
  return `${pathname}${rest === "" ? "" : `?${rest}`}#integrations`;
}

/** Who may connect Jira or Slack: what the settings surface and the checklist both ask. */
export const mayManageIntegrations = (session: SessionResponse, roles: Role[] | null): boolean =>
  onOwnMembership(session) && roleHolds(roles, session.role, "integrations:manage");

/** A disconnected connection is not a connection: only a live row counts. */
export function integrationsStepOf(rows: IntegrationRow[]): IntegrationsStep {
  const live = (kind: AdapterKind) => rows.some((row) => row.kind === kind && row.active);
  return { jira: live("jira"), slack: live("slack") };
}

/**
 * The step for this session, or null when there is none to show: the person could
 * not act on it, or either read did not answer. A read that fails answers null rather
 * than throwing — the checklist it sits under does not depend on it — but only the two
 * reads are caught, so a bug in what follows them is not reported as "no step".
 */
export async function readIntegrationsStep(api: Api, session: SessionResponse): Promise<IntegrationsStep | null> {
  if (!onOwnMembership(session)) return null;
  let read: [IntegrationRow[], Role[]];
  try {
    read = await Promise.all([listIntegrations(api), listRoles(api)]);
  } catch {
    return null;
  }
  const [rows, roles] = read;
  return mayManageIntegrations(session, roles) ? integrationsStepOf(rows) : null;
}

/**
 * Read the step while `wanted`, and again whenever the session or `epoch` changes,
 * handing each answer to `deliver`. Both screens read it here, so what re-reads it is
 * written once (#627).
 *
 * **Keyed on the session itself, not the context.** Who may connect is the role's, and a
 * session update can change the role and nothing that names the context; a read keyed on
 * the context goes on judging the old role until something else re-reads it.
 *
 * `deliver` is not a key: the console's changes every render. The latest one is used,
 * and an answer that lands after the key moved is dropped.
 */
export function useIntegrationsStep(
  api: Api,
  session: SessionResponse | null,
  wanted: boolean,
  epoch: number,
  deliver: (step: IntegrationsStep | null) => void,
): void {
  const latest = useRef(deliver);
  useEffect(() => {
    latest.current = deliver;
  });
  useEffect(() => {
    if (!wanted || session === null) return;
    let live = true;
    void readIntegrationsStep(api, session).then((step) => {
      if (live) latest.current(step);
    });
    return () => {
      live = false;
    };
  }, [api, session, wanted, epoch]);
}
